<script lang="ts">
	import * as Sidebar from '$shared/components/ui/sidebar/index.js';
	import * as Collapsible from '$shared/components/ui/collapsible/index.js';
	import { page } from '$app/state';
	import { docsNavigation } from '$lib/data/docs-data';
	import { submitNewsletter, LeadApiError } from '$lib/api/leads';
	import DocsSearch from './DocsSearch.svelte';

	const currentSlug = $derived(page.url.pathname.replace('/docs/', ''));

	// All sections expanded by default
	let expandedSections = $state<Set<string>>(new Set(docsNavigation.map((s) => s.id)));

	function toggleSection(sectionId: string, open: boolean) {
		if (open) {
			expandedSections = new Set([...expandedSections, sectionId]);
		} else {
			const newSet = new Set(expandedSections);
			newSet.delete(sectionId);
			expandedSections = newSet;
		}
	}

	// Newsletter subscription
	let email = $state('');
	let submitting = $state(false);
	let subscribed = $state(false);
	let error = $state('');

	async function handleSubscribe(e: Event) {
		e.preventDefault();
		if (!email.trim() || submitting) return;

		submitting = true;
		error = '';

		try {
			await submitNewsletter(email);
			subscribed = true;
			email = '';
		} catch (err) {
			if (err instanceof LeadApiError) {
				error = err.isRateLimited ? 'Please wait a moment and try again.' : 'Something went wrong.';
			} else {
				error = 'Something went wrong.';
			}
		} finally {
			submitting = false;
		}
	}
</script>

<Sidebar.Root variant="sidebar" collapsible="none" class="border-r border-[#e5e5e5] bg-[#F9F9F4]">
	<Sidebar.Header class="p-4">
		<DocsSearch />
	</Sidebar.Header>

	<Sidebar.Content class="pb-4">
		{#each docsNavigation as section}
			<Collapsible.Root
				open={expandedSections.has(section.id)}
				onOpenChange={(open) => toggleSection(section.id, open ?? false)}
			>
				<Sidebar.Group>
					<Collapsible.Trigger class="w-full">
						<Sidebar.GroupLabel
							class="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-sidebar-accent/50 transition-colors"
						>
							<span class="font-mono text-[11px] font-semibold uppercase tracking-wider text-[#3a3a3a]">
								{section.title}
							</span>
							<svg
								class="w-4 h-4 text-[#878787] transition-transform"
								class:rotate-90={expandedSections.has(section.id)}
								viewBox="0 0 16 16"
								fill="none"
							>
								<path
									d="M6 4L10 8L6 12"
									stroke="currentColor"
									stroke-width="1.5"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</Sidebar.GroupLabel>
					</Collapsible.Trigger>

					<Collapsible.Content>
						<Sidebar.Menu class="px-2">
							{#each section.pages as docPage}
								{@const isActive = currentSlug === docPage.slug}
								<Sidebar.MenuItem>
									<Sidebar.MenuButton {isActive} class="pl-4">
										{#snippet child({ props })}
											<a
												href="/docs/{docPage.slug}"
												{...props}
												class="flex w-full items-center py-1.5 px-3 text-[14px] rounded transition-colors {isActive
													? 'bg-sidebar-accent text-[#ee6018] font-medium'
													: 'text-[#5c5855] hover:bg-sidebar-accent/50 hover:text-[#020202]'}"
											>
												{docPage.title}
											</a>
										{/snippet}
									</Sidebar.MenuButton>
								</Sidebar.MenuItem>
							{/each}
						</Sidebar.Menu>
					</Collapsible.Content>
				</Sidebar.Group>
			</Collapsible.Root>
		{/each}
	</Sidebar.Content>

	<Sidebar.Footer class="mt-auto p-4 border-t border-[#e5e5e5] space-y-4">
		<!-- Newsletter signup -->
		<div class="space-y-2">
			<p class="font-mono text-[10px] uppercase tracking-wider text-[#878787]">Stay updated</p>
			{#if subscribed}
				<p class="text-[12px] text-[#5c5855]">Thanks for subscribing!</p>
			{:else}
				<form onsubmit={handleSubscribe} class="flex gap-1.5">
					<input
						type="email"
						bind:value={email}
						placeholder="Email"
						required
						class="flex-1 min-w-0 px-2.5 py-1.5 text-[12px] bg-white border border-[#e0e0e0] rounded placeholder:text-[#a0a0a0] focus:outline-none focus:border-[#ee6018]"
					/>
					<button
						type="submit"
						disabled={submitting}
						class="px-2.5 py-1.5 text-[11px] font-medium bg-[#020202] text-white rounded hover:bg-[#333] disabled:opacity-50 transition-colors"
					>
						{submitting ? '...' : 'Subscribe'}
					</button>
				</form>
				{#if error}
					<p class="text-[11px] text-red-500">{error}</p>
				{/if}
			{/if}
		</div>

		<!-- Back to site link -->
		<a
			href="/"
			class="flex items-center gap-2 text-[12px] text-[#878787] hover:text-[#ee6018] transition-colors"
		>
			<svg class="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
				<path
					d="M10 4L6 8L10 12"
					stroke="currentColor"
					stroke-width="1.5"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			Back to site
		</a>
	</Sidebar.Footer>
</Sidebar.Root>
